import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { setRemoteContentDefault } from "../actions";
import {
  isAdmin,
  passwordChangeEnabled,
  passwordMaxLength,
  passwordMinLength,
  remoteContentDefault,
  showSettings,
  userEmail,
} from "../state";
import type { AccountKeyInfo, ApiTokenInfo, AuditEntry, RemoteContentDefault, RetentionPolicy, SoftDeleteEntry } from "../../shared/types";
import { Icon } from "./icons";

type Tab = "password" | "privacy" | "tokens" | "appPasswords" | "admin";

/** Mox native account page shown when self-service change is disabled. */
const MOX_ACCOUNT_URL = "https://mail.gmhost.es/account/";

export function Settings(): JSX.Element | null {
  if (!showSettings.value) return null;

  const adminVisible = isAdmin.value;
  const [activeTab, setActiveTab] = useState<Tab>("password");

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) showSettings.value = false;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
    >
      <div class="glass flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl shadow-2xl ring-1 ring-strong">
        {/* Header */}
        <div class="flex shrink-0 items-center justify-between border-b border-strong px-6 py-4">
          <h2 class="text-base font-semibold text-fg">Settings</h2>
          <button
            class="icon-btn"
            onClick={() => {
              showSettings.value = false;
            }}
            aria-label="Close"
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {/* Tab bar — always rendered; every user has Password + Tokens + App Passwords. */}
        <div class="flex shrink-0 gap-1 border-b border-strong px-6 pt-2">
          <TabButton label="Password" active={activeTab === "password"} onClick={() => setActiveTab("password")} />
          <TabButton label="Privacy" active={activeTab === "privacy"} onClick={() => setActiveTab("privacy")} />
          <TabButton label="API Tokens" active={activeTab === "tokens"} onClick={() => setActiveTab("tokens")} />
          <TabButton label="App Passwords" active={activeTab === "appPasswords"} onClick={() => setActiveTab("appPasswords")} />
          {/* Admin tab stays gated — never exposed to non-admins. */}
          {adminVisible && (
            <TabButton label="Admin" active={activeTab === "admin"} onClick={() => setActiveTab("admin")} />
          )}
        </div>

        {/* Body */}
        <div class="flex min-h-0 flex-1 overflow-y-auto p-6">
          {activeTab === "password" && <PasswordPanel />}
          {activeTab === "privacy" && <RemoteContentPanel />}
          {activeTab === "tokens" && <ApiTokensPanel />}
          {activeTab === "appPasswords" && <AppPasswordsPanel />}
          {activeTab === "admin" && adminVisible && <AdminPanel />}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      class={`px-3 pb-2 text-sm font-medium transition-colors ${
        active
          ? "border-b-2 border-accent text-accent"
          : "text-muted hover:text-fg"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Password Panel                                                      */
/* ------------------------------------------------------------------ */

function PasswordPanel(): JSX.Element {
  const enabled = passwordChangeEnabled.value;
  // Policy bounds come from the server session — single source of truth.
  const minLength = passwordMinLength.value;
  const maxLength = passwordMaxLength.value;

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Wipe plaintext passwords from component memory — on success AND on any
  // failed attempt, so nothing lingers after the form is submitted.
  function clearFields(): void {
    setCurrent("");
    setNext("");
    setConfirm("");
  }

  // Feature flag OFF → point users at Mox's native account page instead.
  if (!enabled) {
    return (
      <div class="w-full space-y-6">
        <div>
          <h3 class="mb-1 text-sm font-semibold text-fg">Password</h3>
          <p class="text-xs text-muted">Change the password for your mailbox account.</p>
        </div>
        <div class="rounded-lg border border-subtle p-4 space-y-3">
          <p class="text-sm text-muted">
            Changing your password from here isn't available yet. You can update it on the
            mail server's account page.
          </p>
          <a
            class="btn-accent inline-block"
            href={MOX_ACCOUNT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open account page
          </a>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!current || !next || !confirm) {
      setError("Please fill in all fields.");
      clearFields();
      return;
    }
    if (next.length < minLength || next.length > maxLength) {
      setError(`New password must be between ${minLength} and ${maxLength} characters.`);
      clearFields();
      return;
    }
    if (next !== confirm) {
      setError("New password and confirmation do not match.");
      clearFields();
      return;
    }
    if (next === current) {
      setError("New password must be different from your current password.");
      clearFields();
      return;
    }

    setSubmitting(true);
    try {
      await api.account.changePassword({ currentPassword: current, newPassword: next });
      setSuccess(true);
      clearFields();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to change password.");
      clearFields();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="w-full space-y-6">
      <div>
        <h3 class="mb-1 text-sm font-semibold text-fg">Password</h3>
        <p class="text-xs text-muted">
          Change the password for your mailbox account. It must be{" "}
          {minLength}–{maxLength} characters long.
        </p>
      </div>

      {/* Success notice */}
      {success && (
        <div class="rounded-lg border border-success/40 bg-success/10 p-4">
          <p class="text-xs font-semibold text-success">
            Password changed. You stay signed in — no need to log in again.
          </p>
        </div>
      )}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        class="space-y-3 rounded-lg border border-subtle p-4"
      >
        <div>
          <label class="mb-1 block text-xs font-medium text-muted">Current password</label>
          <input
            class="field text-sm"
            type="password"
            autocomplete="current-password"
            value={current}
            onInput={(e) => setCurrent((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted">New password</label>
          <input
            class="field text-sm"
            type="password"
            autocomplete="new-password"
            minLength={minLength}
            maxLength={maxLength}
            value={next}
            onInput={(e) => setNext((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted">Confirm new password</label>
          <input
            class="field text-sm"
            type="password"
            autocomplete="new-password"
            minLength={minLength}
            maxLength={maxLength}
            value={confirm}
            onInput={(e) => setConfirm((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>
        <button type="submit" class="btn-accent" disabled={submitting}>
          {submitting ? "Changing…" : "Change password"}
        </button>
      </form>

      {error && <p class="text-sm text-danger">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Privacy Panel — remote content default                              */
/* ------------------------------------------------------------------ */

function RemoteContentPanel(): JSX.Element {
  const current = remoteContentDefault.value;
  const [saving, setSaving] = useState<RemoteContentDefault | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSelect(value: RemoteContentDefault): Promise<void> {
    if (value === current || saving !== null) return;
    setError(null);
    setSaving(value);
    try {
      // The action re-throws non-auth failures (R4-001) so we render a local
      // error state instead of silently swallowing it, mirroring PasswordPanel.
      await setRemoteContentDefault(value);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update preference.");
    } finally {
      setSaving(null);
    }
  }

  const options: { value: RemoteContentDefault; label: string; description: string }[] = [
    {
      value: "block",
      label: "Block remote content",
      description:
        "Images and other remote resources are blocked until you choose to load them for a message.",
    },
    {
      value: "always",
      label: "Always show remote content",
      description:
        "Remote images load automatically. Senders may use them as tracking pixels to see when you open mail.",
    },
  ];

  return (
    <div class="w-full space-y-6">
      <div>
        <h3 class="mb-1 text-sm font-semibold text-fg">Remote content</h3>
        <p class="text-xs text-muted">
          Choose whether emails may load remote images and resources by default. Blocking remote
          content protects your privacy by preventing tracking pixels from loading automatically.
        </p>
      </div>

      <div class="space-y-2 rounded-lg border border-subtle p-4" role="radiogroup" aria-label="Remote content default">
        {options.map((opt) => {
          const active = current === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={saving !== null}
              onClick={() => void handleSelect(opt.value)}
              class={`flex w-full flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                active ? "border-accent bg-accent/10" : "border-subtle hover:border-strong"
              }`}
            >
              <div class="flex w-full items-center gap-2">
                <span class="text-sm font-medium text-fg">{opt.label}</span>
                {active && <span class="ml-auto text-xs font-semibold text-accent">Current</span>}
                {saving === opt.value && <span class="ml-auto text-xs text-muted">Saving…</span>}
              </div>
              <span class="text-xs text-muted">{opt.description}</span>
            </button>
          );
        })}
      </div>

      {error && <p class="text-sm text-danger">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  API Tokens Panel                                                    */
/* ------------------------------------------------------------------ */

function ApiTokensPanel(): JSX.Element {
  // Read userEmail signal value — used only to satisfy React rules but not directly shown
  void userEmail.value;

  const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [newToken, setNewToken] = useState<string | null>(null);

  // Create form state
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState<"read" | "send" | "full">("read");
  const [expiryDays, setExpiryDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.tokens
      .list()
      .then(setTokens)
      .catch((e: unknown) =>
        setFetchError(e instanceof Error ? e.message : "Failed to load tokens"),
      )
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setFormError(null);
    try {
      const result = await api.tokens.create({
        label: label.trim(),
        scope,
        expiresInDays: expiryDays ? Number(expiryDays) : undefined,
      });
      setNewToken(result.token);
      setLabel("");
      setExpiryDays("");
      // Refresh list
      const updated = await api.tokens.list();
      setTokens(updated);
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Failed to create token");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: number): Promise<void> {
    try {
      await api.tokens.revoke(id);
      setTokens((t) => t.filter((tok) => tok.id !== id));
    } catch (e: unknown) {
      setFetchError(e instanceof Error ? e.message : "Failed to revoke token");
    }
  }

  return (
    <div class="w-full space-y-6">
      <div>
        <h3 class="mb-1 text-sm font-semibold text-fg">API Tokens</h3>
        <p class="text-xs text-muted">
          Tokens let external tools access your mailbox via the API. Bearer authentication:{" "}
          <code class="rounded bg-ink-3 px-1 text-xs">Authorization: Bearer twm_...</code>
        </p>
      </div>

      {/* New token shown once */}
      {newToken && (
        <div class="rounded-lg border border-success/40 bg-success/10 p-4">
          <p class="mb-2 text-xs font-semibold text-success">
            Token created — copy it now, it won't be shown again:
          </p>
          <pre class="break-all rounded bg-ink-3 p-2 text-xs text-fg select-all">{newToken}</pre>
          <button class="mt-2 text-xs text-muted hover:text-fg" onClick={() => setNewToken(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Create form */}
      <form
        onSubmit={(e) => void handleCreate(e)}
        class="space-y-3 rounded-lg border border-subtle p-4"
      >
        <div>
          <label class="mb-1 block text-xs font-medium text-muted">Label</label>
          <input
            class="field text-sm"
            placeholder="e.g. CI pipeline, Home script…"
            value={label}
            onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="mb-1 block text-xs font-medium text-muted">Scope</label>
            <select
              class="field text-sm"
              value={scope}
              onChange={(e) =>
                setScope((e.currentTarget as HTMLSelectElement).value as typeof scope)
              }
            >
              <option value="read">read — list &amp; search only</option>
              <option value="send">send — read + compose</option>
              <option value="full">full — all actions</option>
            </select>
          </div>
          <div class="w-36">
            <label class="mb-1 block text-xs font-medium text-muted">Expires in (days)</label>
            <input
              class="field text-sm"
              placeholder="Never"
              type="number"
              min="1"
              value={expiryDays}
              onInput={(e) => setExpiryDays((e.currentTarget as HTMLInputElement).value)}
            />
          </div>
        </div>
        <button type="submit" class="btn-accent" disabled={creating}>
          {creating ? "Creating…" : "Create token"}
        </button>
      </form>

      {formError && <p class="text-sm text-danger">{formError}</p>}
      {fetchError && <p class="text-sm text-danger">{fetchError}</p>}

      {/* Token list */}
      {loading ? (
        <p class="text-sm text-muted">Loading…</p>
      ) : tokens.length === 0 ? (
        <p class="text-sm text-muted">No tokens yet. Create one above.</p>
      ) : (
        <div class="space-y-2">
          {tokens.map((tok) => (
            <div key={tok.id} class="flex items-center gap-3 rounded-lg border border-subtle p-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium text-fg">{tok.label}</span>
                  <span
                    class={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                      tok.scope === "full"
                        ? "bg-danger/20 text-danger"
                        : tok.scope === "send"
                          ? "bg-warn/20 text-warn"
                          : "bg-ink-3 text-muted"
                    }`}
                  >
                    {tok.scope}
                  </span>
                  {tok.revoked && (
                    <span class="shrink-0 rounded-full bg-danger/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-danger">
                      revoked
                    </span>
                  )}
                </div>
                <div class="mt-0.5 text-xs text-muted">
                  Created {new Date(tok.createdAt).toLocaleDateString()}
                  {tok.lastUsedAt &&
                    ` · Last used ${new Date(tok.lastUsedAt).toLocaleDateString()}`}
                  {tok.expiresAt &&
                    ` · Expires ${new Date(tok.expiresAt).toLocaleDateString()}`}
                </div>
              </div>
              {!tok.revoked && (
                <button
                  class="shrink-0 rounded-md px-2.5 py-1 text-xs text-danger hover:bg-danger/10"
                  onClick={() => void handleRevoke(tok.id)}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  App Passwords Panel                                                 */
/* ------------------------------------------------------------------ */

function AppPasswordsPanel(): JSX.Element {
  const [keys, setKeys] = useState<AccountKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.accountKeys
      .list()
      .then(setKeys)
      .catch((e: unknown) =>
        setFetchError(e instanceof Error ? e.message : "Failed to load app passwords"),
      )
      .finally(() => setLoading(false));
  }, []);

  async function handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setFormError(null);
    try {
      const result = await api.accountKeys.create({
        label: label.trim(),
        expiresInDays: expiryDays ? Number(expiryDays) : undefined,
      });
      setNewKey(result.key);
      setLabel("");
      setExpiryDays("");
      const updated = await api.accountKeys.list();
      setKeys(updated);
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Failed to create app password");
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke(id: number): Promise<void> {
    try {
      await api.accountKeys.revoke(id);
      setKeys((k) => k.filter((key) => key.id !== id));
    } catch (e: unknown) {
      setFetchError(e instanceof Error ? e.message : "Failed to revoke app password");
    }
  }

  return (
    <div class="w-full space-y-6">
      <div>
        <h3 class="mb-1 text-sm font-semibold text-fg">App Passwords</h3>
        <p class="text-xs text-muted">
          Named credentials stored for your account. These are stored securely and can be revoked
          at any time.
        </p>
        <p class="mt-1 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
          App passwords are not yet enforced at login — this is a preview feature.
        </p>
      </div>

      {/* New key shown once */}
      {newKey && (
        <div class="rounded-lg border border-success/40 bg-success/10 p-4">
          <p class="mb-2 text-xs font-semibold text-success">
            App password created — copy it now, it won't be shown again:
          </p>
          <pre class="break-all rounded bg-ink-3 p-2 text-xs text-fg select-all">{newKey}</pre>
          <button class="mt-2 text-xs text-muted hover:text-fg" onClick={() => setNewKey(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Create form */}
      <form
        onSubmit={(e) => void handleCreate(e)}
        class="space-y-3 rounded-lg border border-subtle p-4"
      >
        <div>
          <label class="mb-1 block text-xs font-medium text-muted">Label</label>
          <input
            class="field text-sm"
            placeholder="e.g. Mobile app, Thunderbird…"
            value={label}
            onInput={(e) => setLabel((e.currentTarget as HTMLInputElement).value)}
            required
          />
        </div>
        <div class="w-36">
          <label class="mb-1 block text-xs font-medium text-muted">Expires in (days)</label>
          <input
            class="field text-sm"
            placeholder="Never"
            type="number"
            min="1"
            value={expiryDays}
            onInput={(e) => setExpiryDays((e.currentTarget as HTMLInputElement).value)}
          />
        </div>
        <button type="submit" class="btn-accent" disabled={creating}>
          {creating ? "Creating…" : "Create app password"}
        </button>
      </form>

      {formError && <p class="text-sm text-danger">{formError}</p>}
      {fetchError && <p class="text-sm text-danger">{fetchError}</p>}

      {loading ? (
        <p class="text-sm text-muted">Loading…</p>
      ) : keys.length === 0 ? (
        <p class="text-sm text-muted">No app passwords yet. Create one above.</p>
      ) : (
        <div class="space-y-2">
          {keys.map((k) => (
            <div key={k.id} class="flex items-center gap-3 rounded-lg border border-subtle p-3">
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="truncate text-sm font-medium text-fg">{k.label}</span>
                  {k.revoked && (
                    <span class="shrink-0 rounded-full bg-danger/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-danger">
                      revoked
                    </span>
                  )}
                </div>
                <div class="mt-0.5 text-xs text-muted">
                  Created {new Date(k.createdAt).toLocaleDateString()}
                  {k.expiresAt &&
                    ` · Expires ${new Date(k.expiresAt).toLocaleDateString()}`}
                </div>
              </div>
              {!k.revoked && (
                <button
                  class="shrink-0 rounded-md px-2.5 py-1 text-xs text-danger hover:bg-danger/10"
                  onClick={() => void handleRevoke(k.id)}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Admin Panel                                                         */
/* ------------------------------------------------------------------ */

interface AdminUser {
  email: string;
  localpart: string;
}

interface AdminToken {
  id: number;
  label: string;
  email: string;
  scope: string;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
  revoked: boolean;
}

function AdminPanel(): JSX.Element {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tokens, setTokens] = useState<AdminToken[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [tokensError, setTokensError] = useState<string | null>(null);

  // Audit log state
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Soft deletes state
  const [softDeletes, setSoftDeletes] = useState<SoftDeleteEntry[]>([]);
  const [loadingSoftDeletes, setLoadingSoftDeletes] = useState(true);
  const [softDeletesError, setSoftDeletesError] = useState<string | null>(null);

  // Retention policy state
  const [policies, setPolicies] = useState<RetentionPolicy[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [policiesError, setPoliciesError] = useState<string | null>(null);
  const [newMailboxPath, setNewMailboxPath] = useState("");
  const [newMaxAgeDays, setNewMaxAgeDays] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyFormError, setPolicyFormError] = useState<string | null>(null);

  useEffect(() => {
    void api.admin
      .users()
      .then(setUsers)
      .catch((e: unknown) =>
        setUsersError(e instanceof Error ? e.message : "Failed to load users"),
      )
      .finally(() => setLoadingUsers(false));

    void api.admin
      .tokens()
      .then(setTokens)
      .catch((e: unknown) =>
        setTokensError(e instanceof Error ? e.message : "Failed to load tokens"),
      )
      .finally(() => setLoadingTokens(false));

    void api.admin
      .audit(100)
      .then((res) => {
        setAuditEntries(res.entries);
        setAuditTotal(res.total);
      })
      .catch((e: unknown) =>
        setAuditError(e instanceof Error ? e.message : "Failed to load audit log"),
      )
      .finally(() => setLoadingAudit(false));

    void api.admin
      .softDeletes(100)
      .then(setSoftDeletes)
      .catch((e: unknown) =>
        setSoftDeletesError(e instanceof Error ? e.message : "Failed to load soft deletes"),
      )
      .finally(() => setLoadingSoftDeletes(false));

    void api.admin.retention
      .list()
      .then(setPolicies)
      .catch((e: unknown) =>
        setPoliciesError(e instanceof Error ? e.message : "Failed to load retention policies"),
      )
      .finally(() => setLoadingPolicies(false));
  }, []);

  async function handleAddPolicy(e: Event): Promise<void> {
    e.preventDefault();
    const days = Number(newMaxAgeDays);
    if (!newMailboxPath.trim() || !days || days < 1) return;
    setSavingPolicy(true);
    setPolicyFormError(null);
    try {
      const updated = await api.admin.retention.upsert({
        mailboxPath: newMailboxPath.trim(),
        maxAgeDays: days,
      });
      setPolicies(updated);
      setNewMailboxPath("");
      setNewMaxAgeDays("");
    } catch (e: unknown) {
      setPolicyFormError(e instanceof Error ? e.message : "Failed to save policy");
    } finally {
      setSavingPolicy(false);
    }
  }

  async function handleDeletePolicy(mailboxPath: string): Promise<void> {
    try {
      await api.admin.retention.delete(mailboxPath);
      setPolicies((p) => p.filter((pol) => pol.mailboxPath !== mailboxPath));
    } catch (e: unknown) {
      setPoliciesError(e instanceof Error ? e.message : "Failed to delete policy");
    }
  }

  return (
    <div class="w-full space-y-8">
      {/* Users section */}
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-fg">Users</h3>
        {usersError && <p class="text-sm text-danger">{usersError}</p>}
        {loadingUsers ? (
          <p class="text-sm text-muted">Loading…</p>
        ) : users.length === 0 ? (
          <p class="text-sm text-muted">No users found.</p>
        ) : (
          <div class="overflow-x-auto rounded-lg border border-subtle">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-subtle bg-ink-2">
                  <th class="px-3 py-2 text-left font-medium text-muted">Email</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Localpart</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.email} class="border-b border-subtle last:border-0">
                    <td class="px-3 py-2 text-fg">{u.email}</td>
                    <td class="px-3 py-2 text-muted">{u.localpart}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All tokens section */}
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-fg">All API Tokens</h3>
        {tokensError && <p class="text-sm text-danger">{tokensError}</p>}
        {loadingTokens ? (
          <p class="text-sm text-muted">Loading…</p>
        ) : tokens.length === 0 ? (
          <p class="text-sm text-muted">No tokens found.</p>
        ) : (
          <div class="space-y-2">
            {tokens.map((tok) => (
              <div key={tok.id} class="flex items-center gap-3 rounded-lg border border-subtle p-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-sm font-medium text-fg">{tok.label}</span>
                    <span
                      class={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                        tok.scope === "full"
                          ? "bg-danger/20 text-danger"
                          : tok.scope === "send"
                            ? "bg-warn/20 text-warn"
                            : "bg-ink-3 text-muted"
                      }`}
                    >
                      {tok.scope}
                    </span>
                    {tok.revoked && (
                      <span class="shrink-0 rounded-full bg-danger/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-danger">
                        revoked
                      </span>
                    )}
                  </div>
                  <div class="mt-0.5 text-xs text-muted">
                    <span class="font-medium text-fg-dim">{tok.email}</span>
                    {" · "}Created {new Date(tok.createdAt).toLocaleDateString()}
                    {tok.lastUsedAt &&
                      ` · Last used ${new Date(tok.lastUsedAt).toLocaleDateString()}`}
                    {tok.expiresAt &&
                      ` · Expires ${new Date(tok.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit Log section */}
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-fg">
          Audit Log
          {auditTotal > 0 && (
            <span class="ml-2 text-xs font-normal text-muted">({auditTotal} total)</span>
          )}
        </h3>
        {auditError && <p class="text-sm text-danger">{auditError}</p>}
        {loadingAudit ? (
          <p class="text-sm text-muted">Loading…</p>
        ) : auditEntries.length === 0 ? (
          <p class="text-sm text-muted">No audit entries yet.</p>
        ) : (
          <div class="overflow-x-auto rounded-lg border border-subtle">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-subtle bg-ink-2">
                  <th class="px-3 py-2 text-left font-medium text-muted">Time</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Actor</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Action</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Detail</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">IP</th>
                </tr>
              </thead>
              <tbody>
                {auditEntries.map((entry) => (
                  <tr key={entry.id} class="border-b border-subtle last:border-0">
                    <td class="px-3 py-2 text-muted whitespace-nowrap">
                      {new Date(entry.ts).toLocaleString()}
                    </td>
                    <td class="px-3 py-2 text-fg">{entry.actor}</td>
                    <td class="px-3 py-2">
                      <span class="rounded bg-ink-3 px-1.5 py-0.5 font-mono text-fg">
                        {entry.action}
                      </span>
                    </td>
                    <td class="px-3 py-2 text-muted">{entry.detail ?? "—"}</td>
                    <td class="px-3 py-2 text-muted">{entry.ip ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Soft Deletes section */}
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-fg">Soft Deletes</h3>
        {softDeletesError && <p class="text-sm text-danger">{softDeletesError}</p>}
        {loadingSoftDeletes ? (
          <p class="text-sm text-muted">Loading…</p>
        ) : softDeletes.length === 0 ? (
          <p class="text-sm text-muted">No soft-deleted messages recorded.</p>
        ) : (
          <div class="overflow-x-auto rounded-lg border border-subtle">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-subtle bg-ink-2">
                  <th class="px-3 py-2 text-left font-medium text-muted">Email</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Mailbox</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">UID</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Subject</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Deleted At</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Expunged</th>
                </tr>
              </thead>
              <tbody>
                {softDeletes.map((sd) => (
                  <tr key={sd.id} class="border-b border-subtle last:border-0">
                    <td class="px-3 py-2 text-fg">{sd.email}</td>
                    <td class="px-3 py-2 text-muted">{sd.mailbox}</td>
                    <td class="px-3 py-2 text-muted font-mono">{sd.uid}</td>
                    <td class="px-3 py-2 text-muted">{sd.subject ?? "—"}</td>
                    <td class="px-3 py-2 text-muted whitespace-nowrap">
                      {new Date(sd.deletedAt).toLocaleString()}
                    </td>
                    <td class="px-3 py-2">
                      {sd.expunged ? (
                        <span class="rounded-full bg-danger/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-danger">
                          yes
                        </span>
                      ) : (
                        <span class="text-muted">no</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Retention Policies section */}
      <div class="space-y-3">
        <h3 class="text-sm font-semibold text-fg">Retention Policies</h3>
        {policiesError && <p class="text-sm text-danger">{policiesError}</p>}

        {/* Add policy form */}
        <form
          onSubmit={(e) => void handleAddPolicy(e)}
          class="flex flex-wrap gap-2 rounded-lg border border-subtle p-4"
        >
          <input
            class="field h-8 flex-1 min-w-[160px] text-xs"
            placeholder="Mailbox path (e.g. INBOX)"
            value={newMailboxPath}
            onInput={(e) => setNewMailboxPath((e.currentTarget as HTMLInputElement).value)}
            required
          />
          <input
            class="field h-8 w-32 text-xs"
            placeholder="Max age (days)"
            type="number"
            min="1"
            value={newMaxAgeDays}
            onInput={(e) => setNewMaxAgeDays((e.currentTarget as HTMLInputElement).value)}
            required
          />
          <button type="submit" class="btn-accent h-8 px-3 text-xs" disabled={savingPolicy}>
            {savingPolicy ? "Saving…" : "Add / Update"}
          </button>
        </form>
        {policyFormError && <p class="text-sm text-danger">{policyFormError}</p>}

        {loadingPolicies ? (
          <p class="text-sm text-muted">Loading…</p>
        ) : policies.length === 0 ? (
          <p class="text-sm text-muted">No retention policies configured.</p>
        ) : (
          <div class="overflow-x-auto rounded-lg border border-subtle">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-subtle bg-ink-2">
                  <th class="px-3 py-2 text-left font-medium text-muted">Mailbox Path</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Max Age (days)</th>
                  <th class="px-3 py-2 text-left font-medium text-muted">Created</th>
                  <th class="px-3 py-2 text-left font-medium text-muted"></th>
                </tr>
              </thead>
              <tbody>
                {policies.map((pol) => (
                  <tr key={pol.id} class="border-b border-subtle last:border-0">
                    <td class="px-3 py-2 text-fg font-mono">{pol.mailboxPath}</td>
                    <td class="px-3 py-2 text-muted">{pol.maxAgeDays}</td>
                    <td class="px-3 py-2 text-muted whitespace-nowrap">
                      {new Date(pol.createdAt).toLocaleDateString()}
                    </td>
                    <td class="px-3 py-2 text-right">
                      <button
                        class="rounded-md px-2.5 py-1 text-xs text-danger hover:bg-danger/10"
                        onClick={() => void handleDeletePolicy(pol.mailboxPath)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
